import { toast } from "@arcadeai/design-system/components/ui/molecules/toast";
import { LoginPage as DesignSystemLoginPage } from "@arcadeai/design-system/components/ui/pages/login-page";
import {
  getSafeReturnTo,
  REGISTRATION_PROMPT,
  RETURN_TO_PARAM,
} from "@arcadeai/ui-kit/utils/auth-redirect";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { RootLoader } from "@/components/custom/root-loader";
import { authClient, sessionQueryOptions } from "@/lib/auth-client";
import {
  ACCOUNT_UI_URL,
  LOGIN_REASON_PARAM,
  SESSION_EXPIRED_REASON,
} from "@/lib/const";
import { queryClient } from "@/lib/query-client";
import { refreshSessionName } from "@/lib/session-name";
import { whoAmIQueryOptions } from "@/lib/strong-auth-reminder";

// `auto_login` is set by identity-ui to start the OIDC handshake immediately
// instead of stranding the user on this page — silently when an identity
// session exists, or on the provider screen selected by `prompt`
// (registration keeps account creation inside the OAuth2 challenge, so the
// new account is signed in when the flow completes).
const searchBooleanSchema = z.union([
  z.boolean(),
  z.enum(["true", "false"]).transform((value) => value === "true"),
]);

const searchParamsSchema = z.object({
  auto_login: searchBooleanSchema.catch(false).default(false),
  return_to: z.string().optional().catch(undefined),
  prompt: z.enum([REGISTRATION_PROMPT]).optional().catch(undefined),
  [LOGIN_REASON_PARAM]: z
    .enum([SESSION_EXPIRED_REASON])
    .optional()
    .catch(undefined),
});

function manualLoginSearch({
  prompt,
  returnTo,
}: {
  prompt?: typeof REGISTRATION_PROMPT;
  returnTo: string;
}) {
  return {
    return_to: returnTo,
    prompt,
  };
}

export const Route = createFileRoute("/login")({
  validateSearch: searchParamsSchema,
  pendingComponent: RootLoader,
  beforeLoad: async ({ search }) => {
    const session = await queryClient.fetchQuery({
      ...sessionQueryOptions,
      staleTime: 0,
    });
    const returnTo = getSafeReturnTo(search.return_to, window.location.origin);
    if (session) {
      // identity-ui's Back to dashboard comes through here, possibly after a
      // profile edit the session hasn't seen.
      if (search.auto_login) {
        await refreshSessionName({
          session: session.user,
          fetchWhoAmI: () =>
            queryClient.fetchQuery({ ...whoAmIQueryOptions, staleTime: 0 }),
          updateUser: (name) => authClient.updateUser({ name }),
          invalidateSession: () =>
            queryClient.invalidateQueries({
              queryKey: sessionQueryOptions.queryKey,
              exact: true,
              refetchType: "all",
            }),
        });
      }
      throw redirect({ href: returnTo });
    }
    if (search.auto_login) {
      let authorizeUrl: string | undefined;
      try {
        const { data } = await authClient.signIn.oauth2({
          providerId: "oidc",
          callbackURL: returnTo,
          disableRedirect: true,
          additionalData: search.prompt ? { prompt: search.prompt } : undefined,
        });
        authorizeUrl = data?.url ?? undefined;
      } catch {
        // Fall through to the manual sign-in button.
      }
      if (authorizeUrl) {
        throw redirect({ href: authorizeUrl });
      }
      throw redirect({
        to: "/login",
        search: manualLoginSearch({ prompt: search.prompt, returnTo }),
      });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const {
    return_to: returnTo,
    prompt,
    [LOGIN_REASON_PARAM]: reason,
  } = Route.useSearch();
  const [isLoading, setIsLoading] = useState(false);

  // The auth-recovery fallback lands the user here when silent re-auth fails.
  // Explain why, so an unexpected trip back to sign-in has context. The reason
  // is fixed for this mount (the redirect sets it once), so this runs once.
  // Stable id keeps it single under StrictMode's double effect invocation.
  const showSessionExpiredNotice = reason === SESSION_EXPIRED_REASON;
  useEffect(() => {
    if (showSessionExpiredNotice) {
      toast.info("Your session expired. Please sign in to continue.", {
        id: "session-expired-notice",
        duration: 8000,
      });
    }
  }, [showSessionExpiredNotice]);

  const registerUrl = new URL("/registerOrRedirect", ACCOUNT_UI_URL);
  if (returnTo) {
    registerUrl.searchParams.set(
      RETURN_TO_PARAM,
      getSafeReturnTo(returnTo, window.location.origin)
    );
  }

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const { error } = await authClient.signIn.oauth2({
        providerId: "oidc",
        callbackURL: getSafeReturnTo(returnTo, window.location.origin),
        additionalData: prompt ? { prompt } : undefined,
      });
      if (error) {
        toast.error("Sign-in failed. Please try again.");
        setIsLoading(false);
      }
    } catch {
      toast.error("Sign-in failed. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <DesignSystemLoginPage
      isLoading={isLoading}
      onSubmit={handleSubmit}
      registerHref={registerUrl.toString()}
    />
  );
}
